import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { KatSpinner } from '@amzn/katal-react';
import { applyTheme } from '@amzn/awsui-components-core/theming';
import AppInitWrapper from './components/AppInitWrapper';
import { App } from './components/App';
import { StencilProvider, ThemeMode } from '@amzn/stencil-react-components/context';
import defaultStencilTokens from '@amzn/stencil-design-tokens/js/web/theme/stencilTheme';
import { usePersistedState } from './hooks/useLocalStorage';
import { eLocalStorageKeys } from './constants/AppConstants';
import { ThemeContextProvider } from './components/context/ThemeContextProvider';
import { APP_THEME } from './constants/ThemeConstants';

import './index.scss';

// Apply theme before React mounts to prevent FOUC (flash of unstyled content)
applyTheme({ theme: APP_THEME });

// Override Stencil's default font (Amazon Ember) with Ember Modern Text to match Cloudscape theming
const STENCIL_THEME = {
  ...defaultStencilTokens,
  ':root': {
    ...defaultStencilTokens[':root'],
    '--st-dt-text-family-default': 'Ember Modern Text'
  }
};

export const StencilWithTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use the same localStorage key as DFPTopNavigation
  const [isDarkModeEnabled] = usePersistedState(eLocalStorageKeys.IsDarkModeEnabled, false);
  const themeMode = isDarkModeEnabled ? ThemeMode.Dark : ThemeMode.Light;
  return (
    <StencilProvider themeMode={themeMode} theme={STENCIL_THEME}>
      {children}
    </StencilProvider>
  );
};

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOMClient.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ThemeContextProvider>
        <StencilWithTheme>
          <AppInitWrapper>
            <React.Suspense fallback={<KatSpinner size="large" />}>
              <App />
            </React.Suspense>
          </AppInitWrapper>
        </StencilWithTheme>
      </ThemeContextProvider>
    </React.StrictMode>
  );
}
